import { useCallback, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const IDLE_MS = 15 * 60 * 1000;
const WARN_MS = 60 * 1000;
const KEY = "bb_last_activity";
const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];

const now = () => Date.now();
const lastActivity = () => Number(localStorage.getItem(KEY)) || now();

export function IdleGuard() {
  const { logout } = useAuth();
  const [left, setLeft] = useState(null);
  const lastWrite = useRef(0);

  const touch = useCallback(() => {
    if (now() - lastWrite.current < 5000) return;
    lastWrite.current = now();
    localStorage.setItem(KEY, String(now()));
  }, []);

  const stay = () => {
    lastWrite.current = 0;
    touch();
    setLeft(null);
  };

  useEffect(() => { localStorage.setItem(KEY, String(now())); }, []);
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    const onActivity = () => { if (left === null) touch(); };
    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(async () => {
      const idle = now() - lastActivity();
      if (idle >= IDLE_MS) {
        clearInterval(timer);
        localStorage.removeItem(KEY);
        await logoutRef.current();
        toast.info("You were signed out after 15 minutes of inactivity to protect client information.");
      } else if (idle >= IDLE_MS - WARN_MS) {
        setLeft(Math.ceil((IDLE_MS - idle) / 1000));
      } else {
        setLeft(null);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [left, touch]);

  return (
    <Dialog open={left !== null} onOpenChange={(o) => { if (!o) stay(); }}>
      <DialogContent className="sm:max-w-md" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Still there?</DialogTitle>
          <DialogDescription>
            For privacy, you'll be signed out in <b className="text-slate-900 tabular-nums" data-testid="idle-countdown">{left}s</b> because
            there's been no activity. Save any note you're working on.
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-full" onClick={() => logout()} data-testid="idle-signout-button">Sign out now</Button>
          <Button className="rounded-full" onClick={stay} data-testid="idle-stay-button">Stay signed in</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
